// The top filter bar. Leagues: League + Season drive the widgets. Players: a
// player search box is the anchor, with Competition + Season constrained to that
// player (§7B decision 4). Each section owns its own scope (passed in from
// CanvasShell); a widget reacts only to the filters it `consumes`.

import { useEffect } from "react";
import type { LayoutSection } from "@catbento/shared";
import { theme } from "../lib/theme.js";
import { useCompetitions, useSeasons } from "../data/leagues.js";
import { usePlayerCompetitions, usePlayerSeasons } from "../data/players.js";
import type { LeaguesScope } from "../filters/leaguesScope.js";
import type { PlayersScope } from "../filters/playersScope.js";
import LeagueSelect from "./LeagueSelect.js";
import SeasonSelect from "./SeasonSelect.js";
import PlayerSearchSelect from "./PlayerSearchSelect.js";

function Bar({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: theme.tile,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      {children}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span style={{ color: theme.inkMuted, fontSize: 12 }}>{children}</span>;
}

function LeaguesFilters({ scope }: { scope: LeaguesScope }) {
  const comps = useCompetitions();
  const seasons = useSeasons(scope.competitionId);
  const seasonList = seasons.data?.seasons ?? [];

  useEffect(() => {
    if (seasonList.length === 0) return;
    const valid = scope.seasonId !== null && seasonList.some((s) => s.id === scope.seasonId);
    if (!valid) scope.setSeasonId(seasonList[0]!.id);
  }, [seasonList, scope]);

  return (
    <Bar>
      <FieldLabel>League</FieldLabel>
      <LeagueSelect
        competitions={comps.data?.competitions ?? []}
        selectedId={scope.competitionId}
        onSelect={scope.setCompetitionId}
        loading={comps.isPending}
      />
      <FieldLabel>Season</FieldLabel>
      <SeasonSelect
        seasons={seasonList}
        selectedId={scope.seasonId}
        onSelect={scope.setSeasonId}
        loading={seasons.isPending && scope.competitionId !== null}
      />
      {comps.isError && <span style={{ color: theme.danger, fontSize: 12 }}>Couldn't load leagues.</span>}
    </Bar>
  );
}

function PlayersFilters({ scope }: { scope: PlayersScope }) {
  const comps = usePlayerCompetitions(scope.playerId);
  const seasons = usePlayerSeasons(scope.playerId, scope.competitionId);
  const compList = comps.data?.competitions ?? [];
  const seasonList = seasons.data?.seasons ?? [];

  // Keep competition valid for the player (default to their first if unset/stale).
  useEffect(() => {
    if (scope.playerId === null || compList.length === 0) return;
    const valid = scope.competitionId !== null && compList.some((c) => c.id === scope.competitionId);
    if (!valid) scope.setCompetitionId(compList[0]!.id);
  }, [compList, scope]);

  // Keep season valid for the player+competition (default to latest).
  useEffect(() => {
    if (seasonList.length === 0) return;
    const valid = scope.seasonId !== null && seasonList.some((s) => s.id === scope.seasonId);
    if (!valid) scope.setSeasonId(seasonList[0]!.id);
  }, [seasonList, scope]);

  return (
    <Bar>
      <FieldLabel>Player</FieldLabel>
      <PlayerSearchSelect
        currentName={scope.playerName}
        onSelect={(p) => scope.selectPlayer(p.id, p.name, p.mostRecentCompetitionId, p.mostRecentSeasonId)}
      />
      {scope.playerId !== null && (
        <>
          <FieldLabel>Competition</FieldLabel>
          <select
            aria-label="Select a competition"
            value={scope.competitionId ?? ""}
            disabled={comps.isPending || compList.length === 0}
            onChange={(e) => scope.setCompetitionId(Number(e.target.value))}
            style={{
              background: theme.input,
              color: theme.ink,
              border: `1px solid ${theme.borderInput}`,
              borderRadius: 8,
              padding: "8px 12px",
              fontSize: 14,
              maxWidth: 220,
            }}
          >
            {compList.length === 0 && <option value="">—</option>}
            {compList.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <FieldLabel>Season</FieldLabel>
          <SeasonSelect
            seasons={seasonList}
            selectedId={scope.seasonId}
            onSelect={scope.setSeasonId}
            loading={seasons.isPending && scope.competitionId !== null}
          />
        </>
      )}
    </Bar>
  );
}

export default function FilterBar({
  section,
  leaguesScope,
  playersScope,
}: {
  section: LayoutSection;
  leaguesScope: LeaguesScope;
  playersScope: PlayersScope;
}) {
  return section === "leagues" ? (
    <LeaguesFilters scope={leaguesScope} />
  ) : (
    <PlayersFilters scope={playersScope} />
  );
}
